import React from "react";
import { Draggable } from "react-beautiful-dnd";
import { Popover, Tooltip } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import moment from "moment";
import { useSelector } from "react-redux";

import StepPopup from "./StepPopup";
import StepContent from "./StepContent";
import markerIcons from "../Markers/markerIcons";
import { getTranslation } from "../../utils/helper";

import { convertToInt, getWidthChart, convertMinutesToPixel } from "./Utils";
import { COLOR_DEFAULT, TIME_FORMAT_FRONTEND } from "../../constants";
import { ReactSVG } from "react-svg";

const DriverListSteps = ({
  driver,
  route,
  routePlan,
  steps,
  minutes,
  startTime,
  onClickStep,
  onClickRoute,
  handleCloseStepPopup,
  moveToUnscheduled,
  stopsHash,
  stepsHovered,
  setStepsHovered,
  onHoverStep,
  onHoverRoute,
  routeType,
  setShowMoreRoute,
  oneHourPx,
  isSameDate,
  selectedMultipleSteps,
  setAllInclusiveMultipleSteps,
  multipleStepState,
  stepClicked,
  indexRoute,
}) => {
  const shiftStart = route.shiftStart || steps[0]?.arrivalTime;
  const shiftEnd = route.shiftEnd || steps[steps.length - 1]?.finishTime;

  const widthStart = getWidthChart(startTime, shiftStart, oneHourPx);
  const marginLeftWidth = getWidthChart(startTime, moment().format(TIME_FORMAT_FRONTEND), oneHourPx);
  const widthEnd = getWidthChart(startTime, shiftEnd, oneHourPx);
  const widthRoad = getWidthChart(shiftStart, shiftEnd, oneHourPx);
  const defaultColor = COLOR_DEFAULT;

  const breakStart = route.optimizedBreakStart || route.breakStart;
  const breakEnd = route.optimizedBreakEnd || route.breakEnd;
  let widthBreak, marginLeftBreak;

  if (
    breakStart &&
    breakEnd &&
    moment(shiftEnd, TIME_FORMAT_FRONTEND).isAfter(moment(breakStart, TIME_FORMAT_FRONTEND))
  ) {
    widthBreak = getWidthChart(breakStart, breakEnd, oneHourPx);
    marginLeftBreak = getWidthChart(startTime, breakStart, oneHourPx);
  }

  const user = useSelector(state => state.auth.user);
  const setting = user.company;

  return (
    <div className="driver-row">
      <Popover
        open={(stepsHovered[driver._id] && routeType === "start") || false}
        content={
          <div>
            {route.startAddress}
            <div className="actions">
              <span onClick={() => setShowMoreRoute(true)} style={{ margin: 0 }}>
                {getTranslation(setting, "Common", "Show more")} <ArrowRightOutlined />
              </span>
            </div>
          </div>
        }>
        <div
          className="truck-start"
          onClick={() => onClickRoute(route, "start")}
          onMouseOver={() => onHoverRoute(route, "start")}
          onMouseOut={() => handleCloseStepPopup()}
          style={{ left: widthStart - 17 }}>
          <img src={markerIcons["liveDriver"][driver.color || defaultColor]} alt="truck" />
        </div>
      </Popover>
      <div
        className="line"
        style={{
          width: widthRoad,
          left: widthStart,
          background: `#${driver.color || defaultColor}`,
        }}></div>
      <Popover
        open={(stepsHovered[driver._id] && routeType === "end") || false}
        content={
          <div>
            {route.endAddress}
            <div className="actions">
              <span onClick={() => setShowMoreRoute(true)} style={{ margin: 0 }}>
                {getTranslation(setting, "Common", "Show more")} <ArrowRightOutlined />
              </span>
            </div>
          </div>
        }>
        <div
          className="truck-end"
          onClick={() => onClickRoute(route, "end")}
          onMouseOver={() => onHoverRoute(route, "end")}
          onMouseOut={() => handleCloseStepPopup()}
          style={{ left: widthEnd - 11 }}>
          <img src={markerIcons["end"][driver.color || defaultColor]} alt="truck" />
        </div>
      </Popover>
      <div className="steps" style={{ minWidth: widthRoad }}>
        {steps.map((step, indexStep) => {
          const keyDraggable = `draggable${route.driver._id}${indexStep}`;

          const stepPopover = `StepPopover-${step.orderId}-${step.type.toLowerCase()}`;

          const stop = stopsHash[`${step.orderId}-${step.type.toLowerCase()}`];

          if (!stop) return;

          stop.arrivalTime = step.arrivalTime;
          stop.finishTime = step.finishTime;

          const endShift = convertToInt(shiftEnd);
          const endTime = convertToInt(stop.endTime);
          const finishTime = convertToInt(stop.finishTime);
          const arrivalTimeSecs = convertToInt(step.arrivalTime);
          const isLate =
            (finishTime >= 0 && endTime >= 0 && finishTime > endTime) ||
            (finishTime >= 0 && endShift >= 0 && finishTime > endShift) ||
            (stop.endTime && arrivalTimeSecs > endTime);
          const width = stop.arrivalTime ? getWidthChart(stop.arrivalTime, stop.finishTime, oneHourPx) : "24px";

          const isCompleted = ["COMPLETED", "SKIPPED", "PICKUP_FAILED", "DELIVERY_FAILED"].includes(stop.status);
          const keyDraggableCompleted = `${keyDraggable}${isCompleted ? "-isCompleted" : ""}`;
          const stepPopoverCompleted = `${stepPopover}${isCompleted ? "-isCompleted" : ""}`;
          const widthDuration = convertMinutesToPixel(step.duration, oneHourPx);
          return (
            <React.Fragment key={keyDraggableCompleted}>
              <Draggable
                key={keyDraggableCompleted}
                draggableId={keyDraggableCompleted}
                index={indexStep}
                isDragDisabled={isCompleted}>
                {provided => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                      zIndex: stepsHovered[stepPopoverCompleted] ? 999 : 99,
                      width: stepsHovered[stepPopoverCompleted] && widthDuration < 24 ? 24 : width,
                      minWidth: 0,
                      position: "relative",
                      marginLeft: step.marginLeft + (indexStep === 0 ? widthStart : 0),
                      visibility: isCompleted ? "hidden" : "",
                      ...provided.draggableProps.style,
                    }}>
                    <Popover
                      key={indexStep}
                      open={stepsHovered[stepPopoverCompleted] || false}
                      content={
                        <StepPopup
                          route={route}
                          stop={stop}
                          step={step}
                          stepPopover={stepPopoverCompleted}
                          setStepsHovered={setStepsHovered}
                          moveToUnscheduled={moveToUnscheduled}
                          onClickStep={onClickStep}
                          indexSteps={indexStep}
                          setting={setting}
                        />
                      }>
                      <StepContent
                        stop={stop}
                        step={step}
                        stepPopover={stepPopoverCompleted}
                        onClickStep={onClickStep}
                        onHoverStep={onHoverStep}
                        handleCloseStepPopup={handleCloseStepPopup}
                        isLate={isLate}
                        isHover={stepsHovered[stepPopoverCompleted] || false}
                        oneHourPx={oneHourPx}
                        widthDuration={widthDuration}
                        selectedMultipleSteps={selectedMultipleSteps}
                        setAllInclusiveMultipleSteps={setAllInclusiveMultipleSteps}
                        multipleStepState={multipleStepState}
                        driverId={driver._id}
                        stepClicked={stepClicked}
                        breakEnd={breakEnd}
                        breakStart={breakStart}
                      />
                    </Popover>
                  </div>
                )}
              </Draggable>
              {isCompleted && (
                <Draggable key={keyDraggable} draggableId={keyDraggable} index={indexStep} isDragDisabled={isCompleted}>
                  {provided => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        zIndex: stepsHovered[stepPopover] ? 999 : 99,
                        width: width,
                        minWidth: 1,
                        position: "absolute",
                        left: getWidthChart(
                          startTime,
                          moment(stop.completedAt).format(TIME_FORMAT_FRONTEND),
                          oneHourPx,
                        ),
                        ...provided.draggableProps.style,
                      }}>
                      <Popover
                        key={indexStep}
                        open={stepsHovered[stepPopover] || false}
                        content={
                          <StepPopup
                            route={route}
                            stop={stop}
                            step={step}
                            stepPopover={stepPopover}
                            setStepsHovered={setStepsHovered}
                            moveToUnscheduled={moveToUnscheduled}
                            onClickStep={onClickStep}
                            indexSteps={indexStep}
                            setting={setting}
                          />
                        }>
                        <StepContent
                          stop={stop}
                          step={step}
                          stepPopover={stepPopover}
                          onClickStep={onClickStep}
                          onHoverStep={onHoverStep}
                          handleCloseStepPopup={handleCloseStepPopup}
                          isLate={isLate}
                          isHover={stepsHovered[stepPopoverCompleted] || false}
                          oneHourPx={oneHourPx}
                        />
                      </Popover>
                    </div>
                  )}
                </Draggable>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {isSameDate && <div className="now" style={{ marginLeft: marginLeftWidth }}></div>}
      {routePlan?.type === "VRP" && widthBreak && marginLeftBreak && (
        <Tooltip placement="top" title={`${breakStart} - ${breakEnd}`}>
          <div
            className="break"
            style={{
              marginLeft: marginLeftBreak,
              position: "absolute",
              width: widthBreak,
              border: `1px solid #${driver.color}`,
              background: `#${driver.color}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <ReactSVG src={"/icons/break.svg"} className="icon" />
          </div>
        </Tooltip>
      )}
      <div className={`all-minutes ${indexRoute % 2 != 0 ? "white" : "black"}`}>
        {minutes.map((minute, index) => {
          return <div className="minutes" key={index} style={{ width: `${oneHourPx / 2}px` }}></div>;
        })}
      </div>
    </div>
  );
};

export default DriverListSteps;
